/**
 * AccessibleDocCacheStore — Cached doc list + metadata for cloud workspaces.
 *
 * Single in-memory source for the sidebar doc list in cloud workspaces.
 * Local persistence is delegated to WorkspaceMetadataStore; permissions live in
 * PermissionSnapshotStore.
 *
 * sl-jsx3.5: GraphQL-based doc list
 * sl-jsx3.14.2: IDB-backed cache for offline persistence
 * sl-jsx3.14.3: Reconnect trigger + revocation detection
 * sl-jsx3.18: Robust refresh — visibility change, periodic sync, retry backoff
 *
 * Local-First guarantees:
 * - Locally created docs are immediately visible (addLocalDoc)
 * - Cache persists across reload via WorkspaceMetadataStore
 * - DocsStore.setDocMeta() propagates real-time title/trash changes
 * - GraphQL sync merges server state when back online
 * - Reconnect triggers fresh fetch + revocation diff
 * - Visibility change (tab focus) triggers refresh for cross-client sync
 * - Periodic background sync every 30s as safety net
 */

// Direct import to avoid circular dependency (doc → cloud → workspace → doc)
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import { LiveData, Store } from '@toeverything/infra';
import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { Array as YArray, Map as YMap, transact } from 'yjs';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import {
  isBackendAuthOrPermissionError,
  isBackendKnownUnavailable,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import {
  defaultWorkspaceApiClient,
  WorkspaceApiError,
} from '../../cloud/services/workspace-api-client';
import { getLocalAuthorityClientInstallId } from '../../local-authority';
import type {
  PermissionSnapshotResponse,
  PermissionSnapshotStore,
} from '../../permissions/stores/permission-snapshot';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import { resolveWorkspaceLocalAuthorityAccountId } from '../../workspace/utils/permission-cache-identity';
import { readPermissionGraphSnapshotWithCatchUp } from '../../workspace/utils/permission-graph-catch-up';
import {
  getPermissionGraphSnapshotVersion,
  mergePermissionGraphVersion,
  shouldIgnorePermissionGraphSnapshot,
} from '../../workspace/utils/permission-graph-version';
import { filterUserVisibleDocIds } from '../../workspace/utils/user-visible-docs';
import type {
  WorkspaceListMetadata,
  WorkspaceMetadataDeltaPage,
  WorkspaceMetadataItem,
  WorkspaceMetadataPage,
  WorkspaceMetadataPendingMutation,
} from '../../workspace-metadata';
import {
  fetchWorkspaceMetadataDeltaHeadToken,
  fetchWorkspaceMetadataDeltaPage,
  WorkspaceMetadataStore,
} from '../../workspace-metadata';
import { recordWorkspaceProjectionDiagnostic } from '../../workspace-projection-reconciliation/diagnostics';
import type {
  WorkspaceMetadataChange,
  WorkspacePermissionChange,
} from '../../workspace-projection-reconciliation/types';

// Local renames are persisted asynchronously through Yjs/outbox. During that
// window, older GraphQL/IDB reads must not roll the visible title backwards.
const LOCAL_TITLE_WRITE_PROTECTION_MS = 60_000;
const LOCAL_TRASH_WRITE_PROTECTION_MS = 60_000;
const CREATE_METADATA_EVENT_GRACE_MS = 5_000;
const METADATA_EVENT_BURST_WINDOW_MS = 50;

const markAccessibleDocCachePerformanceSignal = (signal: string) => {
  if (
    typeof document === 'undefined' ||
    document.documentElement.dataset.streamlainCanvasPerformanceRecording !==
      'true'
  ) {
    return;
  }
  performance.mark(`streamlain:accessible-doc-cache:${signal}`);
};

const hasSameAccessibleDocMetadata = (
  current: AccessibleDocMeta[],
  next: AccessibleDocMeta[]
) =>
  current.length === next.length &&
  current.every((doc, index) => {
    const candidate = next[index];
    return (
      candidate !== undefined &&
      doc.id === candidate.id &&
      doc.title === candidate.title &&
      doc.trashed === candidate.trashed &&
      doc.parentId === candidate.parentId &&
      doc.parentTrashed === candidate.parentTrashed &&
      doc.createDate === candidate.createDate &&
      doc.updatedDate === candidate.updatedDate &&
      doc.isDemo === candidate.isDemo &&
      doc.permissionGraphVersion === candidate.permissionGraphVersion
    );
  });

export interface AccessibleDocMeta {
  id: string;
  title: string;
  trashed: boolean;
  parentId?: string | null;
  parentTrashed?: boolean;
  createDate: number;
  updatedDate: number;
  isDemo?: boolean;
  permissionGraphVersion?: number;
}

export interface AccessibleDocTrashSnapshot {
  entries: Array<{
    id: string;
    cacheMeta: AccessibleDocMeta | null;
    localMetaExisted: boolean;
    localTrash: boolean | undefined;
    localTrashDate: unknown;
  }>;
}

export type WorkspaceMetadataDocView = 'all' | 'trash';

type AccessibleDocPermanentDeleteSnapshot =
  AccessibleDocTrashSnapshot['entries'][number] & {
    locallyCreated: boolean;
  };

type FetchDocsByIdsOptions = {
  diagnosticReason?: string;
  permissionGraphVersion?: number;
  removeMissing?: boolean;
  revalidateIfCoalesced?: boolean;
};

interface UpdateDocMetaOptions {
  persistTrashState?: boolean;
}

export class AccessibleDocCacheStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly globalState?: GlobalState,
    private readonly permissionSnapshotStore?: PermissionSnapshotStore
  ) {
    super();
  }

  private readonly cache$ = new LiveData<AccessibleDocMeta[]>([]);

  private publishCache(nextDocs: AccessibleDocMeta[], reason: string): void {
    const previousDocs = this.cache$.value;
    const unchanged = hasSameAccessibleDocMetadata(previousDocs, nextDocs);
    if (
      typeof document !== 'undefined' &&
      document.documentElement.dataset.streamlainCanvasPerformanceRecording ===
        'true'
    ) {
      markAccessibleDocCachePerformanceSignal(
        `publish:${reason}:${unchanged ? 'unchanged' : 'changed'}`
      );
    }
    if (unchanged) {
      return;
    }
    this.cache$.setValue(nextDocs);
  }

  private readonly lifecycleAbortController = new AbortController();
  private disposed = false;
  private fetchTriggered = false;
  private fetchAccessibleDocsPromise: Promise<void> | null = null;
  private readonly fetchDocsByIdsPromises = new Map<string, Promise<void>>();
  private readonly fetchDocsByIdsFollowUpPromises = new Map<
    string,
    Promise<void>
  >();
  private readonly activeFetchDocsByIdsPromises = new Set<Promise<void>>();
  private readonly pendingCreateMetadataEvents = new Map<string, number>();
  private readonly serverConfirmationWaiters = new Map<
    string,
    Set<() => void>
  >();
  private readonly serverConfirmationFlights = new Map<
    string,
    Promise<boolean>
  >();
  private readonly recentlyConfirmedLocalDocIds = new Map<string, number>();
  private idbLoaded = false;
  private serverFetchCompleted = false;
  private authoritativeAccessDenied = false;
  private permissionGraphVersion = 0;
  private readonly pendingLocalTitleWrites = new Map<
    string,
    { title: string; expiresAt: number }
  >();
  private readonly pendingLocalTrashWrites = new Map<
    string,
    { trashed: boolean; expiresAt: number }
  >();
  private metadataDeltaToken: string | null = null;
  private readonly metadataViewNextCursors = new Map<
    WorkspaceMetadataDocView,
    string | null
  >();
  private readonly fetchNextMetadataPagePromises = new Map<
    WorkspaceMetadataDocView,
    Promise<boolean>
  >();
  private permissionRefreshPromise: Promise<void> = Promise.resolve();
  private permissionRefreshDepth = 0;
  private completedAuthoritativeProjectionVersion = 0;

  readonly hasNextMetadataPage$ = new LiveData<
    Record<WorkspaceMetadataDocView, boolean>
  >({ all: false, trash: false });

  observePermissionGraphVersion(version?: number | null): void {
    this.permissionGraphVersion = mergePermissionGraphVersion(
      this.permissionGraphVersion,
      version ?? 0
    );
    if (version != null) {
      this.permissionSnapshotStore?.observePermissionGraphVersion(version);
    }
  }

  /**
   * True once the cache has been populated from any source (IDB, server, or addLocalDoc).
   * Used by DocsStore.watchDocListReady() so cloud workspaces don't block on
   * the Y.js root-doc sync state that is irrelevant for the GraphQL-based doc list.
   */
  readonly isPopulated$ = new LiveData<boolean>(false);

  /**
   * sl-jsx3.17.2: Indicates the cache is from IDB only — server has not yet confirmed.
   * True after IDB load but before first successful server fetch.
   * False after server data has been received (or if IDB was never loaded).
   */
  readonly isStale$ = new LiveData<boolean>(false);

  /**
   * True after the account-scoped IndexedDB permission cache has been checked.
   * This is intentionally weaker than isPopulated$: it lets layout code wait
   * for warm local data without blocking on a server fetch when no local doc
   * cache exists yet.
   */
  readonly isLocalCacheLoaded$ = new LiveData<boolean>(false);

  /**
   * True after the first successful server fetch. Consumers that remove docs
   * from local Y.js collections must wait for this stronger signal; IDB-only
   * caches can be stale or incomplete during workspace boot.
   */
  readonly isServerConfirmed$ = new LiveData<boolean>(false);

  /**
   * sl-jsx3.14.3: IDs of docs whose access was revoked on last server sync.
   * Emitted once per fetchAccessibleDocs() call, then reset.
   * DocPermissionService subscribes to this to show notifications.
   */
  readonly revokedDocIds$ = new LiveData<string[]>([]);

  /**
   * Server-confirmed permanent deletions. Unlike revokedDocIds$, this is an
   * explicit lifecycle signal and may drive local-only Y.Doc eviction.
   */
  readonly permanentlyDeletedDocIds$ = new LiveData<string[]>([]);

  /**
   * Tracks locally-created doc IDs to avoid false revocation detection.
   * Docs added via addLocalDoc() won't be in the server response until
   * the server confirms them, so they must be excluded from revocation diff.
   * Also used by GuardService to grant immediate access to self-created docs.
   */
  private readonly locallyCreatedIds = new Set<string>();

  /**
   * sl-o0ra: Tracks doc IDs pending permanent deletion.
   * Prevents fetchAccessibleDocs from re-adding deleted docs before
   * the server mutation completes. Cleared after successful server delete.
   */
  private readonly pendingDeletions = new Set<string>();
  private readonly pendingMetadataRefreshes = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly confirmedDeletedIds = new Set<string>();
  private readonly signaledPermanentDeleteIds = new Set<string>();
  private readonly folderPermanentDeleteSnapshots = new Map<
    string,
    AccessibleDocPermanentDeleteSnapshot
  >();
  private permanentDeleteHandler: ((docId: string) => Promise<unknown>) | null =
    null;

  /**
   * Check if a doc was created locally in this session (not yet server-confirmed).
   * Used by GuardService to bypass the async permission check for the creator.
   */
  isLocallyCreated(docId: string): boolean {
    return this.locallyCreatedIds.has(docId);
  }

  /**
   * Mark a cloud doc before folder placement becomes locally visible. Folder
   * subscriptions can emit synchronously; without this marker they start a
   * metadata read before the local doc cache is published.
   */
  markLocalDocCreating(docId: string): void {
    this.locallyCreatedIds.add(docId);
  }

  private reconcileServerConfirmedLocalDocs(
    visibleServerIds: ReadonlySet<string>
  ): void {
    for (const id of this.locallyCreatedIds) {
      if (visibleServerIds.has(id)) {
        this.locallyCreatedIds.delete(id);
        const waiters = this.serverConfirmationWaiters.get(id);
        this.serverConfirmationWaiters.delete(id);
        for (const resolve of waiters ?? []) resolve();
      }
    }
  }

  private waitForServerConfirmationSignal(
    docId: string,
    timeoutMs: number
  ): Promise<boolean> {
    if (!this.isLocallyCreated(docId)) return Promise.resolve(true);
    return new Promise(resolve => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const waiters = this.serverConfirmationWaiters.get(docId) ?? new Set();
      const complete = (confirmed: boolean) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        waiters.delete(onConfirmed);
        if (waiters.size === 0) {
          this.serverConfirmationWaiters.delete(docId);
        }
        resolve(confirmed);
      };
      const onConfirmed = () => complete(true);
      waiters.add(onConfirmed);
      this.serverConfirmationWaiters.set(docId, waiters);
      timer = setTimeout(
        () => complete(!this.isLocallyCreated(docId)),
        timeoutMs
      );
      if (!this.isLocallyCreated(docId)) complete(true);
    });
  }

  setPermanentDeleteHandler(
    handler: (docId: string) => Promise<unknown>
  ): void {
    this.permanentDeleteHandler = handler;
    this.processPendingDeletions();
  }

  confirmPermanentDelete(docId: string): void {
    this.confirmedDeletedIds.add(docId);
    this.pendingDeletions.delete(docId);
  }

  async waitForServerConfirmed(
    docId: string,
    options: { timeoutMs?: number; pollMs?: number } = {}
  ): Promise<boolean> {
    const inFlight = this.serverConfirmationFlights.get(docId);
    if (inFlight) {
      return inFlight;
    }

    const flight = this.waitForServerConfirmedOnce(docId, options).finally(
      () => {
        if (this.serverConfirmationFlights.get(docId) === flight) {
          this.serverConfirmationFlights.delete(docId);
        }
      }
    );
    this.serverConfirmationFlights.set(docId, flight);
    return flight;
  }

  private async waitForServerConfirmedOnce(
    docId: string,
    options: { timeoutMs?: number; pollMs?: number }
  ): Promise<boolean> {
    const timeoutMs = options.timeoutMs ?? 5000;
    const pollMs = options.pollMs ?? 250;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (!this.isLocallyCreated(docId)) return true;
      const remainingMs = deadline - Date.now();
      if (
        await this.waitForServerConfirmationSignal(
          docId,
          Math.min(pollMs, remainingMs)
        )
      ) {
        return true;
      }
      await this.fetchDocsByIds([docId]).catch(() => {});
      if (!this.isLocallyCreated(docId)) {
        return true;
      }
    }

    await this.fetchDocsByIds([docId]).catch(() => {});
    return !this.isLocallyCreated(docId);
  }

  /**
   * Reconnect debounce timer to prevent rapid-fire fetches.
   */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineHandler: (() => void) | null = null;
  private backendAvailabilityUnsubscribe: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private metadataStoreRef: {
    scopeKey: string;
    store: WorkspaceMetadataStore;
  } | null = null;
  private metadataPendingWrite: Promise<void> = Promise.resolve();

  private get workspaceId(): string | null {
    const workspace = this.workspaceService.workspace;
    return workspace?.id ?? null;
  }

  private get accountId(): string | null {
    const workspace = this.workspaceService.workspace;
    const server = this.workspaceServerService.server;
    if (!workspace || workspace.flavour === 'local' || !server) {
      return null;
    }
    return resolveWorkspaceLocalAuthorityAccountId({
      liveAccountId: server.confirmedAccountId,
      cachedAccountId: server.cachedAccountId,
      scopeReader: this.globalState,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId: workspace.id,
    });
  }

  private get metadataStore(): WorkspaceMetadataStore | null {
    const workspace = this.workspaceService.workspace;
    const server = this.workspaceServerService.server;
    const accountId = this.accountId;
    if (!workspace || workspace.flavour === 'local' || !server || !accountId) {
      return null;
    }
    const verifiedTenantId =
      this.permissionSnapshotStore?.getVerifiedTenantId();
    if (this.permissionSnapshotStore && !verifiedTenantId) {
      return null;
    }
    // Non-production harnesses may omit the optional authority store. Runtime
    // module wiring always supplies it and therefore requires a verified tenant.
    const tenantId = verifiedTenantId ?? `unbound-test:${workspace.id}`;
    const scope = {
      accountId,
      principalId: accountId,
      serverId: server.id,
      serverBaseUrl: server.baseUrl,
      visibilityMode: 'user_effective' as const,
      permissionGraphVersion: this.permissionGraphVersion,
      workspaceId: workspace.id,
      tenantId,
      shapeKey: 'docs-visible-window',
    };
    const scopeKey = JSON.stringify(scope);
    if (this.metadataStoreRef?.scopeKey === scopeKey) {
      return this.metadataStoreRef.store;
    }
    const store = new WorkspaceMetadataStore(scope);
    this.metadataStoreRef = { scopeKey, store };
    return store;
  }

  private loadMetadataDeltaToken(): string | null {
    if (this.metadataDeltaToken !== null) {
      return this.metadataDeltaToken;
    }
    this.metadataDeltaToken = this.metadataStore?.getShapeState().token ?? null;
    return this.metadataDeltaToken;
  }

  private saveMetadataDeltaToken(token: string | null): void {
    this.metadataDeltaToken = token;
    void this.metadataStore?.updateShapeState({ token }).catch(() => {
      // IDB unavailable: this session keeps the bounded in-memory token.
    });
  }

  /**
   * Load cached metadata from the local WorkspaceMetadataStore on first access.
   * Populates the in-memory cache so the sidebar shows docs instantly after reload.
   */
  private async loadFromLocalMetadataStore(): Promise<void> {
    if (this.idbLoaded) return;
    const startedAt = performance.now();
    recordBootEvent('doc-cache-hydration-started');
    try {
      const permissionStartedAt = performance.now();
      await this.permissionSnapshotStore?.hydrateLocal();
      recordBootEvent('doc-cache-permission-ready', {
        durationMs: performance.now() - permissionStartedAt,
      });
      this.observePermissionGraphVersion(
        this.permissionSnapshotStore?.getPermissionGraphVersion() ?? 0
      );

      const metadataStore = this.metadataStore;
      if (!metadataStore) return;

      this.idbLoaded = true;
      try {
        await metadataStore.hydrate();
        const pageCursors = metadataStore.getShapeState().pageCursors;
        for (const view of ['all', 'trash'] as const) {
          if (Object.hasOwn(pageCursors, view)) {
            this.setMetadataViewNextCursor(view, pageCursors[view], false);
          }
        }
        for (const mutation of metadataStore.getPendingMutations()) {
          if (mutation.op === 'delete') {
            this.pendingDeletions.add(mutation.nodeId);
          }
        }
        const freshCached = metadataStore
          .getVisibleNodes()
          .map(node => this.metadataNodeToMeta(node));
        if (freshCached.length > 0 && this.cache$.value.length === 0) {
          this.publishCache(
            this.filterHardDeletedDocs(
              this.applyLocalDocMetaOverrides(freshCached)
            ),
            'local-metadata-hydration'
          );
          this.permissionGraphVersion = mergePermissionGraphVersion(
            this.permissionGraphVersion,
            getPermissionGraphSnapshotVersion(freshCached)
          );
          this.isPopulated$.setValue(true);
          // sl-jsx3.17.2: Cache loaded from IDB, not yet server-confirmed
          if (!this.serverFetchCompleted) {
            this.isStale$.setValue(true);
          }
        }
        this.processPendingDeletions();
      } catch {
        // IDB unavailable — fall through to server fetch
      }
    } finally {
      // No account/verified-tenant scope means no account-scoped IDB was read.
      // Do not let the workspace shell interpret that as completed hydration.
      if (this.idbLoaded) {
        this.isLocalCacheLoaded$.setValue(true);
      }
      recordBootEvent('doc-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        loaded: this.idbLoaded,
        nodeCount: this.cache$.value.length,
        populated: this.isPopulated$.value,
      });
    }
  }

  private metadataNodeToMeta(node: WorkspaceListMetadata): AccessibleDocMeta {
    return {
      id: node.nodeId,
      title: node.title,
      trashed: Boolean(node.trashedAt),
      parentId: node.parentId,
      parentTrashed: node.parentTrashed,
      createDate: Date.parse(node.updatedAt) || Date.now(),
      updatedDate: Date.parse(node.updatedAt) || Date.now(),
      isDemo: node.isDemo === true,
      permissionGraphVersion: this.permissionGraphVersion,
    };
  }

  private docMetaToMetadataNode(
    workspaceId: string,
    doc: AccessibleDocMeta,
    syncState: WorkspaceListMetadata['syncState']
  ): WorkspaceListMetadata {
    const updatedAt = new Date(doc.updatedDate || Date.now()).toISOString();
    return {
      workspaceId,
      nodeId: doc.id,
      kind: 'doc',
      title: doc.title,
      parentId: doc.parentId ?? null,
      areaId: null,
      sortKey: docSortKey(doc),
      hasChildren: false,
      childCount: null,
      subtreeRevision: updatedAt,
      trashedAt: doc.trashed ? updatedAt : null,
      updatedAt,
      permissionHint: 'read',
      syncState,
      isDemo: doc.isDemo,
      parentTrashed: doc.parentTrashed,
    };
  }

  /**
   * Ensure the cache is populated. First from IDB (instant), then from server (async).
   * Called lazily on first subscription. Also starts all background listeners.
   *
   * Race condition: During SPA boot, this may run before onWorkspaceInitialized()
   * binds the server (WorkspaceServerService.server === null). In that case,
   * fetchAccessibleDocs() returns without data. Exponential backoff retries handle
   * this — the server is typically bound within a few hundred milliseconds.
   */
  private ensureFetched(): void {
    if (this.fetchTriggered || this.disposed) return;
    this.fetchTriggered = true;

    // Start all background listeners
    this.startReconnectListener();
    this.startBackendAvailabilityListener();
    this.startVisibilityListener();

    if (this.serverFetchCompleted) {
      return;
    }

    // Load IDB first (fast, synchronous-ish), then server query
    this.loadFromLocalMetadataStore()
      .then(() => this.fetchAccessibleDocs())
      .then(() => {
        if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
          this.scheduleRetry(0);
        }
      })
      .catch(() => {
        this.scheduleRetry(0);
      });
  }

  prime(): void {
    this.ensureFetched();
  }

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly RETRY_DELAYS_MS = [500, 1000, 2000, 5000];

  /**
   * Exponential backoff retry for the race condition where ensureFetched() runs
   * before WorkspaceServerService.bindServer(). Retries up to 4 times with
   * increasing delays: 500ms, 1s, 2s, 5s.
   */
  private scheduleRetry(attempt: number): void {
    if (this.retryTimer || this.disposed) return;
    if (attempt >= AccessibleDocCacheStore.RETRY_DELAYS_MS.length) {
      // sl-oiya.2.2: Only unblock the ready gate if cache actually has data.
      // Setting isPopulated$=true with an EMPTY cache causes false 404s
      // because DetailPageWrapper shows "Not found" when docListReady=true but doc=null.
      if (!this.isPopulated$.value && this.cache$.value.length > 0) {
        this.isPopulated$.setValue(true);
      }
      return;
    }
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.disposed) return;
      markAccessibleDocCachePerformanceSignal(`retry:fired:${attempt}`);
      this.loadFromLocalMetadataStore()
        .then(() => this.fetchAccessibleDocs())
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(attempt + 1);
          }
        })
        .catch(() => {
          this.scheduleRetry(attempt + 1);
        });
    }, AccessibleDocCacheStore.RETRY_DELAYS_MS[attempt]);
  }

  /**
   * sl-jsx3.14.3: Listen for browser online events to trigger fresh fetch.
   * Debounced to prevent rapid-fire fetches on flaky connections.
   */
  private startReconnectListener(): void {
    if (typeof window === 'undefined') return;

    this.onlineHandler = () => this.scheduleConnectivityRefresh();

    window.addEventListener('online', this.onlineHandler);
  }

  private startBackendAvailabilityListener(): void {
    if (this.backendAvailabilityUnsubscribe) return;

    this.backendAvailabilityUnsubscribe = onBackendAvailable(() => {
      this.scheduleConnectivityRefresh();
    });
  }

  private scheduleConnectivityRefresh(): void {
    if (this.disposed) return;
    markAccessibleDocCachePerformanceSignal('connectivity-refresh:scheduled');
    // Debounce: browser-online and backend-available often fire together.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.disposed) return;
      markAccessibleDocCachePerformanceSignal('connectivity-refresh:fired');
      this.processPendingDeletions();
      this.fetchAccessibleDocs()
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(0);
          }
        })
        .catch(() => {
          // Server still unreachable — backend availability probes will retry.
        });
    }, 1000);
  }

  /**
   * sl-jsx3.18: Refresh on tab focus for cross-client sync.
   * When the user switches from another browser/tab where they created a doc,
   * this ensures the doc list updates immediately without manual reload.
   */
  private startVisibilityListener(): void {
    if (typeof document === 'undefined') return;

    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.serverFetchCompleted) return; // Still in initial boot sequence
      markAccessibleDocCachePerformanceSignal('visibility-refresh:fired');
      this.processPendingDeletions();
      this.fetchAccessibleDocs().catch(() => {
        // Server unreachable — keep showing cached data
      });
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  reconcilePermissionChange(event: WorkspacePermissionChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    markAccessibleDocCachePerformanceSignal(
      `permission-event:${event.changeType}`
    );

    recordWorkspaceProjectionDiagnostic({
      stage: 'doc-cache:permission-received',
      changeType: event.changeType,
      docIds: event.affectedDocIds,
      folderIds: event.affectedFolderIds,
    });

    this.observePermissionGraphVersion(event.permissionGraphVersion);
    if (event.refreshMode === 'lazy') return;

    const affectedDocIds = event.affectedDocIds ?? [];
    const scoped =
      event.refreshMode === 'scoped' ||
      (event.refreshMode !== 'full' &&
        ((event.affectedFolderIds?.length ?? 0) > 0 ||
          affectedDocIds.length > 0));
    if (scoped) {
      // The matching metadata doc_created event arrives after initial title
      // materialization and owns the one metadata read for a new document.
      if (event.changeType === 'doc_created') {
        // This recipient-filtered event is emitted only after the document,
        // folder link and effective permissions are server-materialized. It is
        // therefore the authoritative create handshake for the local creator.
        const locallyCreatedDocIds = affectedDocIds.filter(docId =>
          this.locallyCreatedIds.has(docId)
        );
        this.reconcileServerConfirmedLocalDocs(new Set(affectedDocIds));
        const expiresAt = Date.now() + CREATE_METADATA_EVENT_GRACE_MS;
        for (const docId of locallyCreatedDocIds) {
          this.recentlyConfirmedLocalDocIds.set(docId, expiresAt);
        }
        for (const docId of affectedDocIds) {
          this.pendingCreateMetadataEvents.set(docId, expiresAt);
        }
        return;
      }
      if (affectedDocIds.length > 0) {
        void this.fetchDocsByIds(affectedDocIds, undefined, {
          diagnosticReason: `permission-event:${event.changeType}`,
          permissionGraphVersion: event.permissionGraphVersion,
          removeMissing: true,
        }).catch(() => {
          // Delta/periodic reconciliation retries transient failures.
        });
      }
      return;
    }

    this.queuePermissionProjectionRefresh(event.permissionGraphVersion);
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;

    markAccessibleDocCachePerformanceSignal(
      `metadata-event:${event.changeType}`
    );

    recordWorkspaceProjectionDiagnostic({
      stage: 'doc-cache:metadata-received',
      changeType: event.changeType,
      docIds: event.docId ? [event.docId] : undefined,
    });

    if (event.changeType === 'folder_restored') {
      // A local folder-trash mutation can evict contained document metadata
      // while remote readers retain it. The lifecycle event only exposes the
      // safe folder root, so reconcile the bounded active-doc projection once
      // instead of resetting all docs or leaking every descendant ID.
      void this.fetchAccessibleDocs().catch(() => {});
      return;
    }

    if (
      event.changeType === 'doc_created' ||
      event.changeType === 'doc_renamed' ||
      event.changeType === 'doc_trashed' ||
      event.changeType === 'doc_restored' ||
      event.changeType === 'doc_visibility_changed'
    ) {
      if (event.docId) {
        if (event.changeType === 'doc_created') {
          this.pendingCreateMetadataEvents.delete(event.docId);
          const confirmedUntil = this.recentlyConfirmedLocalDocIds.get(
            event.docId
          );
          if (confirmedUntil !== undefined) {
            this.recentlyConfirmedLocalDocIds.delete(event.docId);
            if (confirmedUntil > Date.now()) {
              return;
            }
          }
        }
        if (
          event.changeType === 'doc_renamed' &&
          this.pendingLocalTitleWrites.has(event.docId)
        ) {
          return;
        }
        this.queueMetadataRefresh(
          event.docId,
          `metadata-event:${event.changeType}`
        );
      } else {
        void this.fetchAccessibleDocs().catch(() => {});
      }
      return;
    }

    if (event.changeType === 'doc_deleted') {
      if (event.docId) {
        void this.handleServerPermanentlyDeletedDoc(event.docId).catch(
          () => {}
        );
      } else {
        void this.fetchAccessibleDocs().catch(() => {});
      }
    }
  }

  private queuePermissionProjectionRefresh(
    permissionGraphVersion?: number
  ): void {
    this.permissionRefreshDepth += 1;
    this.permissionRefreshPromise = this.permissionRefreshPromise
      .then(async () => {
        if (this.disposed) return;
        await this.fetchAccessibleDocsPromise?.catch(() => undefined);
        if (this.disposed) return;
        if (
          permissionGraphVersion !== undefined &&
          this.completedAuthoritativeProjectionVersion >= permissionGraphVersion
        ) {
          return;
        }
        const workspace = this.workspaceService.workspace;
        if (!workspace || workspace.flavour === 'local') return;
        await this.reloadAuthoritativeProjection(
          workspace.id,
          permissionGraphVersion,
          this.lifecycleAbortController.signal
        );
      })
      .catch(() => undefined)
      .finally(() => {
        this.permissionRefreshDepth = Math.max(
          0,
          this.permissionRefreshDepth - 1
        );
      });
  }

  private async reloadAuthoritativeProjection(
    workspaceId: string,
    permissionGraphVersion?: number,
    signal?: AbortSignal
  ): Promise<void> {
    await Promise.allSettled(this.activeFetchDocsByIdsPromises);
    if (this.disposed || signal?.aborted) return;

    const materializedDocIds = this.cache$.value.map(doc => doc.id);
    const refreshedDocIds = new Set<string>();
    await this.resetAuthoritativeProjection(permissionGraphVersion);
    await this.fetchAccessibleDocsInitialWindow(
      workspaceId,
      signal,
      refreshedDocIds
    );

    const docIdsToRevalidate = materializedDocIds.filter(
      docId => !refreshedDocIds.has(docId) && !this.locallyCreatedIds.has(docId)
    );
    if (docIdsToRevalidate.length > 0) {
      await this.fetchDocsByIdsFromServer(docIdsToRevalidate, signal, {
        permissionGraphVersion: this.permissionGraphVersion,
        removeMissing: true,
      });
    }
    await this.refreshMetadataDeltaHeadToken(workspaceId, signal);
    if (!this.disposed && !signal?.aborted) {
      this.completedAuthoritativeProjectionVersion = Math.max(
        this.completedAuthoritativeProjectionVersion,
        permissionGraphVersion ?? 0
      );
    }
  }

  private async resetAuthoritativeProjection(
    permissionGraphVersion?: number
  ): Promise<void> {
    const metadataStore = this.metadataStore;
    await metadataStore?.resetServerProjection();
    this.observePermissionGraphVersion(permissionGraphVersion);
    this.metadataDeltaToken = null;
    for (const view of ['all', 'trash'] as const) {
      this.setMetadataViewNextCursor(view, null, false);
    }
    // Keep the last painted projection mounted while the fresh bounded
    // projection and any materialized out-of-window docs are revalidated.
    // Publishing the reset storage state here briefly removed every document
    // row from the sidebar during permission changes.
  }

  /**
   * Clean up all listeners. Called on store disposal.
   */
  override dispose(): void {
    this.disposed = true;
    this.lifecycleAbortController.abort(
      new DOMException('Workspace metadata store disposed', 'AbortError')
    );
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.backendAvailabilityUnsubscribe) {
      this.backendAvailabilityUnsubscribe();
      this.backendAvailabilityUnsubscribe = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    for (const timer of this.pendingMetadataRefreshes.values()) {
      clearTimeout(timer);
    }
    this.pendingMetadataRefreshes.clear();
    this.serverConfirmationFlights.clear();
    this.recentlyConfirmedLocalDocIds.clear();
    super.dispose();
  }

  private queueMetadataRefresh(docId: string, diagnosticReason: string): void {
    const pending = this.pendingMetadataRefreshes.get(docId);
    if (pending) {
      clearTimeout(pending);
    }
    const timer = setTimeout(() => {
      this.pendingMetadataRefreshes.delete(docId);
      if (this.disposed) return;
      void this.refreshDocMeta(docId, undefined, diagnosticReason).catch(
        () => {}
      );
    }, METADATA_EVENT_BURST_WINDOW_MS);
    this.pendingMetadataRefreshes.set(docId, timer);
  }

  /** sl-2go5: Page size for bounded metadata windows. */
  private static readonly PAGE_SIZE = 100;

  /**
   * Fetch the first bounded window for `/all` and `/trash`, or apply deltas
   * after the initial window. Additional pages are explicit user-driven reads.
   * A partial window never drives revocation decisions.
   */
  async fetchAccessibleDocs(signal?: AbortSignal): Promise<void> {
    if (this.disposed) return;
    if (!signal && this.fetchAccessibleDocsPromise) {
      return this.fetchAccessibleDocsPromise;
    }
    const fetchPromise = this.fetchAccessibleDocsFromServer(
      signal ?? this.lifecycleAbortController.signal
    );
    if (!signal) {
      const trackedPromise = fetchPromise.finally(() => {
        if (this.fetchAccessibleDocsPromise === trackedPromise) {
          this.fetchAccessibleDocsPromise = null;
        }
      });
      this.fetchAccessibleDocsPromise = trackedPromise;
      return trackedPromise;
    }
    return fetchPromise;
  }

  private async fetchAccessibleDocsFromServer(
    signal?: AbortSignal
  ): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('Server not available');
    }

    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;

    try {
      const deltaToken = this.loadMetadataDeltaToken();
      if (deltaToken && this.cache$.value.length > 0) {
        await this.fetchAccessibleDocDeltas(workspace.id, deltaToken, signal);
        this.authoritativeAccessDenied = false;
        return;
      }
      await this.fetchAccessibleDocsInitialWindow(workspace.id, signal);
      await this.refreshMetadataDeltaHeadToken(workspace.id, signal);
      this.authoritativeAccessDenied = false;
    } catch (error) {
      if (isInvalidMetadataDeltaTokenError(error)) {
        this.saveMetadataDeltaToken(null);
        try {
          await this.fetchAccessibleDocsInitialWindow(workspace.id, signal);
          await this.refreshMetadataDeltaHeadToken(workspace.id, signal);
          this.authoritativeAccessDenied = false;
        } catch (fallbackError) {
          if (this.failClosedForAuthoritativeAccessError(fallbackError)) {
            return;
          }
          throw fallbackError;
        }
        return;
      }
      if (this.failClosedForAuthoritativeAccessError(error)) {
        return;
      }
      throw error;
    }
  }

  private async fetchAccessibleDocDeltas(
    workspaceId: string,
    initialToken: string,
    signal?: AbortSignal
  ): Promise<void> {
    if (signal?.aborted) return;
    const result = await fetchWorkspaceMetadataDeltaPage(
      workspaceId,
      initialToken,
      {
        first: AccessibleDocCacheStore.PAGE_SIZE,
        priority: 'visible',
        signal,
      }
    );

    if (result.permissionSnapshot) {
      await this.permissionSnapshotStore?.applyOnlineResponse(
        result.permissionSnapshot
      );
      // The online envelope may make the tenant-scoped local store available
      // for the first time. Merge it before publishing this network page.
      await this.loadFromLocalMetadataStore();
    }

    if (result.resetRequired) {
      await this.reloadAuthoritativeProjection(
        workspaceId,
        result.permissionGraphVersion,
        signal
      );
      return;
    }
    await this.applyAccessibleDocDeltaPage(workspaceId, result);
    if (result.nextToken) {
      this.saveMetadataDeltaToken(result.nextToken);
    }

    this.serverFetchCompleted = true;
    this.isServerConfirmed$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isStale$.setValue(false);
    this.isPopulated$.setValue(true);
  }

  private async applyAccessibleDocDeltaPage(
    workspaceId: string,
    result: WorkspaceMetadataDeltaPage
  ): Promise<void> {
    const batchDocs = this.filterHardDeletedDocs(
      this.applyLocalDocMetaOverrides(
        result.items
          .filter(item => item.type === 'doc')
          .map(this.mapMetadataItemToMeta),
        { authoritativeServer: true }
      )
    );
    const batchPermissionGraphVersion = Math.max(
      getPermissionGraphSnapshotVersion(batchDocs),
      result.permissionGraphVersion ?? 0
    );
    this.permissionSnapshotStore?.observePermissionGraphVersion(
      result.permissionGraphVersion ?? 0
    );
    // Server visibility is sufficient to end the temporary local-creator
    // bypass. Keep this separate from accepting the batch's permission data:
    // a concurrent permission event can make the metadata batch stale while
    // the returned doc still proves that initial server materialization
    // completed.
    this.reconcileServerConfirmedLocalDocs(
      new Set(batchDocs.map(doc => doc.id))
    );
    if (
      shouldIgnorePermissionGraphSnapshot(
        batchPermissionGraphVersion,
        this.permissionGraphVersion
      )
    ) {
      return;
    }
    this.permissionGraphVersion = mergePermissionGraphVersion(
      this.permissionGraphVersion,
      batchPermissionGraphVersion
    );

    this.mergeBatchIntoCache(batchDocs);
    await this.syncBatchToMetadataStore(workspaceId, batchDocs);

    const returnedDocIds = new Set(batchDocs.map(doc => doc.id));
    const changedDocIds = [
      ...new Set(
        result.changes
          .filter(change => change.resourceType === 'doc' && change.resourceId)
          .map(change => change.resourceId as string)
      ),
    ];
    const permanentlyDeletedDocIds = [
      ...new Set(
        result.changes
          .filter(
            change =>
              change.resourceType === 'doc' &&
              change.resourceId &&
              change.changeType === 'doc_deleted'
          )
          .map(change => change.resourceId as string)
      ),
    ];
    const missingDocIds = changedDocIds.filter(
      docId => !returnedDocIds.has(docId)
    );
    await this.removeRevokedDocs(missingDocIds);
    await this.handleServerPermanentlyDeletedDocs(permanentlyDeletedDocIds);
  }

  async handleServerPermanentlyDeletedDoc(docId: string): Promise<void> {
    await this.handleServerPermanentlyDeletedDocs([docId]);
  }

  private async handleServerPermanentlyDeletedDocs(
    docIds: readonly string[]
  ): Promise<void> {
    const uniqueDocIds = [...new Set(docIds)].filter(Boolean);
    if (uniqueDocIds.length === 0) return;

    for (const docId of uniqueDocIds) {
      this.confirmPermanentDelete(docId);
      this.removeDocFromLocalCache(docId);
    }
    await Promise.all(
      uniqueDocIds.map(docId => this.removeDocFromMetadataStore(docId))
    );
    this.emitPermanentDeleteLifecycle(uniqueDocIds);
  }

  private emitPermanentDeleteLifecycle(docIds: readonly string[]): void {
    const newlyDeleted = docIds.filter(docId => {
      if (this.signaledPermanentDeleteIds.has(docId)) return false;
      this.signaledPermanentDeleteIds.add(docId);
      return true;
    });
    if (newlyDeleted.length === 0) return;

    // Deletion also revokes access, preserving existing navigation/guard
    // behavior without treating ordinary permission revocation as deletion.
    this.revokedDocIds$.setValue(newlyDeleted);
    this.permanentlyDeletedDocIds$.setValue(newlyDeleted);
  }

  private async refreshMetadataDeltaHeadToken(
    workspaceId: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      const token = await fetchWorkspaceMetadataDeltaHeadToken(
        workspaceId,
        signal
      );
      this.saveMetadataDeltaToken(token);
    } catch {
      this.saveMetadataDeltaToken(null);
    }
  }

  private async fetchAccessibleDocsInitialWindow(
    workspaceId: string,
    signal?: AbortSignal,
    refreshedDocIds?: Set<string>
  ): Promise<void> {
    let skippedStaleBatch = false;
    let sawAnyServerDoc = false;

    for (const view of ['all', 'trash'] as const) {
      if (signal?.aborted) return;
      const viewResult = await this.fetchAccessibleDocsViewPage(
        workspaceId,
        view,
        null,
        signal,
        refreshedDocIds
      );
      if (viewResult.aborted) return;
      skippedStaleBatch = skippedStaleBatch || viewResult.skippedStaleBatch;
      sawAnyServerDoc = sawAnyServerDoc || viewResult.sawAnyServerDoc;
    }

    if (skippedStaleBatch) {
      return;
    }

    // After last batch: finalize
    this.serverFetchCompleted = true;
    this.isServerConfirmed$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isStale$.setValue(false);

    // If no docs at all were fetched, still mark as populated
    if (!sawAnyServerDoc) {
      this.isPopulated$.setValue(true);
    }
  }

  async fetchNextAccessibleDocsPage(
    view: WorkspaceMetadataDocView,
    signal?: AbortSignal
  ): Promise<boolean> {
    if (this.disposed) return false;
    if (!signal) {
      const pending = this.fetchNextMetadataPagePromises.get(view);
      if (pending) {
        return pending;
      }
    }

    const promise = this.fetchNextAccessibleDocsPageFromServer(
      view,
      signal ?? this.lifecycleAbortController.signal
    );
    if (signal) {
      return promise;
    }

    const tracked = promise.finally(() => {
      if (this.fetchNextMetadataPagePromises.get(view) === tracked) {
        this.fetchNextMetadataPagePromises.delete(view);
      }
    });
    this.fetchNextMetadataPagePromises.set(view, tracked);
    return tracked;
  }

  private async fetchNextAccessibleDocsPageFromServer(
    view: WorkspaceMetadataDocView,
    signal?: AbortSignal
  ): Promise<boolean> {
    const after = this.metadataViewNextCursors.get(view);
    if (!after) {
      return false;
    }

    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      return false;
    }

    const result = await this.fetchAccessibleDocsViewPage(
      workspace.id,
      view,
      after,
      signal
    );
    if (result.aborted || result.skippedStaleBatch) {
      return this.hasNextMetadataPage$.value[view];
    }
    return this.hasNextMetadataPage$.value[view];
  }

  private async fetchAccessibleDocsViewPage(
    workspaceId: string,
    view: WorkspaceMetadataDocView,
    after: string | null,
    signal?: AbortSignal,
    refreshedDocIds?: Set<string>
  ): Promise<{
    aborted: boolean;
    skippedStaleBatch: boolean;
    sawAnyServerDoc: boolean;
  }> {
    let skippedStaleBatch = false;
    let sawAnyServerDoc = false;

    if (signal?.aborted) {
      return {
        aborted: true,
        skippedStaleBatch,
        sawAnyServerDoc,
      };
    }
    const query = new URLSearchParams({
      view,
      first: String(AccessibleDocCacheStore.PAGE_SIZE),
    });
    if (after) {
      query.set('after', after);
    }
    const result: WorkspaceMetadataPage & {
      permissionSnapshot?: PermissionSnapshotResponse;
    } = await defaultWorkspaceApiClient.requestJson<WorkspaceMetadataPage>({
      workspaceId,
      path: `/api/workspaces/${workspaceId}/metadata/docs?${query.toString()}`,
      category: 'metadata',
      key: `metadata-docs:${view}:${after ?? 'first'}`,
      priority: view === 'all' && !after ? 'visible' : 'background',
      mode: 'background',
      init: {
        method: 'GET',
        signal,
        headers: await this.permissionSnapshotRequestHeaders(),
      },
    });

    if (result.permissionSnapshot) {
      await this.permissionSnapshotStore?.applyOnlineResponse(
        result.permissionSnapshot
      );
      // The first online envelope can unlock the tenant-scoped IDB. Hydrate
      // it before the first server page becomes visible to sidebar consumers.
      await this.loadFromLocalMetadataStore();
    }

    const batchDocs: AccessibleDocMeta[] = this.filterHardDeletedDocs(
      this.applyLocalDocMetaOverrides(
        result.items
          .filter(item => item.type === 'doc')
          .map(this.mapMetadataItemToMeta),
        { authoritativeServer: true }
      )
    );
    const batchPermissionGraphVersion = Math.max(
      getPermissionGraphSnapshotVersion(batchDocs),
      result.permissionGraphVersion ?? 0
    );
    this.permissionSnapshotStore?.observePermissionGraphVersion(
      result.permissionGraphVersion ?? 0
    );
    this.reconcileServerConfirmedLocalDocs(
      new Set(batchDocs.map(doc => doc.id))
    );
    for (const doc of batchDocs) {
      refreshedDocIds?.add(doc.id);
    }
    if (
      shouldIgnorePermissionGraphSnapshot(
        batchPermissionGraphVersion,
        this.permissionGraphVersion
      )
    ) {
      skippedStaleBatch = true;
      this.setMetadataViewNextCursor(
        view,
        result.pageInfo.hasNextPage ? result.pageInfo.endCursor : null
      );
      return { aborted: false, skippedStaleBatch, sawAnyServerDoc };
    }
    this.permissionGraphVersion = mergePermissionGraphVersion(
      this.permissionGraphVersion,
      batchPermissionGraphVersion
    );

    this.mergeBatchIntoCache(batchDocs);

    await this.syncBatchToMetadataStore(workspaceId, batchDocs);

    if (batchDocs.length > 0) {
      this.isPopulated$.setValue(true);
      sawAnyServerDoc = true;
    }

    this.setMetadataViewNextCursor(
      view,
      result.pageInfo.hasNextPage ? result.pageInfo.endCursor : null
    );

    return { aborted: false, skippedStaleBatch, sawAnyServerDoc };
  }

  private setMetadataViewNextCursor(
    view: WorkspaceMetadataDocView,
    cursor: string | null,
    persist = true
  ): void {
    this.metadataViewNextCursors.set(view, cursor);
    this.hasNextMetadataPage$.setValue({
      ...this.hasNextMetadataPage$.value,
      [view]: Boolean(cursor),
    });
    if (persist) {
      void this.metadataStore?.updatePageCursor(view, cursor).catch(() => {
        // The in-memory cursor remains usable when local persistence is absent.
      });
    }
  }

  private async permissionSnapshotRequestHeaders(): Promise<HeadersInit> {
    return {
      'x-streamlain-client-install-id': getLocalAuthorityClientInstallId(),
    };
  }

  private failClosedForAuthoritativeAccessError(error: unknown): boolean {
    if (!isBackendAuthOrPermissionError(error) || isBackendKnownUnavailable()) {
      return false;
    }

    const revokedIds = this.cache$.value.map(doc => doc.id);
    this.publishCache([], 'authoritative-access-denied');
    this.permissionGraphVersion = 0;
    this.completedAuthoritativeProjectionVersion = 0;
    this.metadataViewNextCursors.clear();
    this.hasNextMetadataPage$.setValue({ all: false, trash: false });
    this.saveMetadataDeltaToken(null);
    this.authoritativeAccessDenied = true;
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isStale$.setValue(false);
    this.isServerConfirmed$.setValue(false);
    if (revokedIds.length > 0) {
      this.revokedDocIds$.setValue(revokedIds);
    }

    this.metadataStore?.clear().catch(() => {
      // Local metadata cleanup failure must not resurrect in-memory access.
    });
    return true;
  }

  /** sl-2go5.2.2: On-demand fetch of docs for a specific folder. */
  async fetchDocsByIds(
    docIds: string[],
    signal?: AbortSignal,
    options: FetchDocsByIdsOptions = {}
  ): Promise<void> {
    if (this.disposed) return;
    if (docIds.length === 0) return;
    recordWorkspaceProjectionDiagnostic({
      stage: 'doc-cache:fetch-requested',
      reason: options.diagnosticReason ?? 'unspecified',
      docIds: [...new Set(docIds)],
    });
    if (this.permissionRefreshDepth > 0) {
      await this.permissionRefreshPromise;
      if (this.disposed) return;
    }

    const fetchKey = signal ? null : this.getFetchDocsByIdsKey(docIds, options);
    if (fetchKey) {
      const existing = this.fetchDocsByIdsPromises.get(fetchKey);
      if (existing) {
        recordWorkspaceProjectionDiagnostic({
          stage: 'doc-cache:fetch-coalesced',
          reason: options.diagnosticReason ?? 'unspecified',
          docIds: [...new Set(docIds)],
        });
        if (options.revalidateIfCoalesced) {
          const queued = this.fetchDocsByIdsFollowUpPromises.get(fetchKey);
          if (queued) return queued;

          const followUp = existing
            .catch(() => undefined)
            .then(() =>
              this.fetchDocsByIds(docIds, undefined, {
                ...options,
                revalidateIfCoalesced: false,
              })
            )
            .finally(() => {
              if (
                this.fetchDocsByIdsFollowUpPromises.get(fetchKey) === followUp
              ) {
                this.fetchDocsByIdsFollowUpPromises.delete(fetchKey);
              }
            });
          this.fetchDocsByIdsFollowUpPromises.set(fetchKey, followUp);
          recordWorkspaceProjectionDiagnostic({
            stage: 'doc-cache:fetch-follow-up-queued',
            reason: options.diagnosticReason ?? 'unspecified',
            docIds: [...new Set(docIds)],
          });
          return followUp;
        }
        return existing;
      }
    }

    const request = this.fetchDocsByIdsFromServer(
      docIds,
      signal ?? this.lifecycleAbortController.signal,
      options
    );
    let trackedRequest: Promise<void>;
    trackedRequest = request.finally(() => {
      this.activeFetchDocsByIdsPromises.delete(trackedRequest);
      if (
        fetchKey &&
        this.fetchDocsByIdsPromises.get(fetchKey) === trackedRequest
      ) {
        this.fetchDocsByIdsPromises.delete(fetchKey);
      }
    });
    this.activeFetchDocsByIdsPromises.add(trackedRequest);
    if (fetchKey) {
      this.fetchDocsByIdsPromises.set(fetchKey, trackedRequest);
    }
    return trackedRequest;
  }

  private getFetchDocsByIdsKey(
    docIds: string[],
    options: FetchDocsByIdsOptions
  ): string {
    return JSON.stringify({
      docIds: [...new Set(docIds)].sort(),
      removeMissing: options.removeMissing === true,
      permissionGraphVersion: options.permissionGraphVersion ?? null,
    });
  }

  private async fetchDocsByIdsFromServer(
    docIds: string[],
    signal?: AbortSignal,
    options: FetchDocsByIdsOptions = {}
  ): Promise<void> {
    await this.loadFromLocalMetadataStore();

    const server = this.workspaceServerService.server;
    if (!server) return;

    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') return;
    const visibleDocIds = filterUserVisibleDocIds(docIds, workspace.id);
    if (visibleDocIds.length === 0) return;

    recordWorkspaceProjectionDiagnostic({
      stage: 'doc-cache:request-started',
      reason: options.diagnosticReason ?? 'unspecified',
      docIds: visibleDocIds,
    });

    try {
      const docs: AccessibleDocMeta[] = [];
      for (
        let index = 0;
        index < visibleDocIds.length;
        index += AccessibleDocCacheStore.PAGE_SIZE
      ) {
        const chunk = visibleDocIds.slice(
          index,
          index + AccessibleDocCacheStore.PAGE_SIZE
        );
        const result = await readPermissionGraphSnapshotWithCatchUp({
          read: async () => {
            const page = await defaultWorkspaceApiClient.requestJson<
              WorkspaceMetadataPage & {
                permissionSnapshot?: PermissionSnapshotResponse;
              }
            >({
              workspaceId: workspace.id,
              path: `/api/workspaces/${workspace.id}/metadata/docs/by-id`,
              category: 'metadata',
              key: `metadata-docs-by-id:${chunk.join(',')}`,
              priority: 'visible',
              mode: 'background',
              init: {
                method: 'POST',
                signal,
                headers: {
                  'content-type': 'application/json',
                  ...(await this.permissionSnapshotRequestHeaders()),
                },
                body: JSON.stringify({ ids: chunk }),
              },
            });
            if (page.permissionSnapshot) {
              await this.permissionSnapshotStore?.applyOnlineResponse(
                page.permissionSnapshot
              );
            }
            return page;
          },
          getIncomingVersion: page => page.permissionGraphVersion ?? 0,
          getCurrentVersion: () =>
            Math.max(
              this.permissionGraphVersion,
              options.permissionGraphVersion ?? 0
            ),
          signal,
        });
        if (!result) return;
        this.permissionSnapshotStore?.observePermissionGraphVersion(
          result.permissionGraphVersion ?? 0
        );
        docs.push(
          ...this.filterHardDeletedDocs(
            this.applyLocalDocMetaOverrides(
              result.items
                .filter(item => item.type === 'doc')
                .map(this.mapMetadataItemToMeta),
              { authoritativeServer: true }
            )
          )
        );
      }
      await this.applyFetchedDocsByIds(
        workspace.id,
        visibleDocIds,
        docs,
        options
      );
      recordWorkspaceProjectionDiagnostic({
        stage: 'doc-cache:request-applied',
        reason: options.diagnosticReason ?? 'unspecified',
        docIds: visibleDocIds,
      });
    } catch (error) {
      if (this.failClosedForAuthoritativeAccessError(error)) {
        return;
      }
      throw error;
    }
  }

  private async applyFetchedDocsByIds(
    workspaceId: string,
    visibleDocIds: string[],
    docs: AccessibleDocMeta[],
    options: FetchDocsByIdsOptions
  ): Promise<void> {
    const cachedBeforeFetch = new Set(this.cache$.value.map(doc => doc.id));
    const permissionGraphVersion = Math.max(
      getPermissionGraphSnapshotVersion(docs),
      options.permissionGraphVersion ?? 0
    );
    this.reconcileServerConfirmedLocalDocs(new Set(docs.map(doc => doc.id)));
    if (
      shouldIgnorePermissionGraphSnapshot(
        permissionGraphVersion,
        this.permissionGraphVersion
      )
    ) {
      return;
    }
    this.permissionGraphVersion = mergePermissionGraphVersion(
      this.permissionGraphVersion,
      permissionGraphVersion
    );

    this.mergeBatchIntoCache(docs);
    if (options.removeMissing) {
      const returnedDocIds = new Set(docs.map(doc => doc.id));
      const missingDocIds = visibleDocIds.filter(id => !returnedDocIds.has(id));
      await this.removeRevokedDocs(missingDocIds, {
        cachedBeforeFetch,
      });
    }

    await this.syncBatchToMetadataStore(workspaceId, docs);

    if (docs.length > 0) {
      this.isPopulated$.setValue(true);
    }
  }

  async fetchFolderDocs(
    folderDocIds: string[],
    signal?: AbortSignal,
    diagnosticReason = 'folder-doc-prefetch'
  ): Promise<void> {
    const now = Date.now();
    const missingDocIds = [...new Set(folderDocIds)].filter(docId => {
      if (this.getCachedDoc(docId)) return false;
      if (this.locallyCreatedIds.has(docId)) return false;
      const pendingUntil = this.pendingCreateMetadataEvents.get(docId);
      if (pendingUntil === undefined) return true;
      if (pendingUntil > now) return false;
      this.pendingCreateMetadataEvents.delete(docId);
      return true;
    });
    if (missingDocIds.length === 0) {
      recordWorkspaceProjectionDiagnostic({
        stage: 'doc-cache:folder-prefetch-skipped',
        reason: diagnosticReason,
        docIds: [...new Set(folderDocIds)],
      });
      return;
    }
    await this.fetchDocsByIds(missingDocIds, signal, { diagnosticReason });
  }

  /**
   * Refresh one doc's server-authoritative metadata after a workspace meta event.
   * The event payload intentionally contains only the docId; this metadata lookup
   * keeps private doc titles behind the existing RBAC checks.
   */
  async refreshDocMeta(
    docId: string,
    signal?: AbortSignal,
    diagnosticReason = 'refresh-doc-meta'
  ): Promise<void> {
    await this.fetchDocsByIds([docId], signal, {
      diagnosticReason,
      revalidateIfCoalesced: signal === undefined,
    });
  }

  /**
   * Ensure a concrete route/open target is present in the permission-filtered
   * cache. Direct doc URLs and newly seeded workspace docs can be opened before
   * the paginated background list has populated IndexedDB.
   */
  async ensureDocCached(docId: string, signal?: AbortSignal): Promise<boolean> {
    if (this.getCachedDoc(docId)) {
      this.isPopulated$.setValue(true);
      return true;
    }

    await this.loadFromLocalMetadataStore();
    if (this.getCachedDoc(docId)) {
      this.isPopulated$.setValue(true);
      return true;
    }

    await this.fetchFolderDocs([docId], signal);
    if (this.getCachedDoc(docId)) {
      return true;
    }

    if (!this.serverFetchCompleted) {
      await this.fetchAccessibleDocs(signal);
    }
    return this.getCachedDoc(docId) !== null;
  }

  private readonly mapMetadataItemToMeta = (
    item: WorkspaceMetadataItem
  ): AccessibleDocMeta => ({
    id: item.id,
    title: item.title ?? '',
    trashed: item.trashed,
    parentId: item.parentId ?? null,
    parentTrashed: item.parentTrashed === true,
    createDate: Date.parse(item.createdAt) || Date.now(),
    updatedDate: Date.parse(item.updatedAt) || Date.now(),
    isDemo: item.isDemo === true,
    permissionGraphVersion: item.permissionGraphVersion ?? 0,
  });

  private applyLocalDocMetaOverrides(
    docs: AccessibleDocMeta[],
    options: { authoritativeServer?: boolean } = {}
  ): AccessibleDocMeta[] {
    const localTrash = this.readLocalTrashMeta();
    if (localTrash.size === 0 && this.pendingLocalTrashWrites.size === 0) {
      return docs;
    }
    const now = Date.now();
    const reconciledDocs = docs.map(doc => {
      if (options.authoritativeServer) {
        return this.applyPendingLocalTrashWrite(
          doc,
          localTrash.get(doc.id),
          now
        );
      }
      const local = localTrash.get(doc.id);
      if (!local) {
        return doc;
      }
      if (local.trashed) {
        return {
          ...doc,
          trashed: true,
          updatedDate: Math.max(doc.updatedDate, local.updatedDate),
        };
      }
      const isFreshLocalWrite =
        local.updatedDate > 0 &&
        now - local.updatedDate < LOCAL_TRASH_WRITE_PROTECTION_MS;
      if (!isFreshLocalWrite) {
        return doc;
      }
      return {
        ...doc,
        trashed: local.trashed,
        updatedDate: Math.max(doc.updatedDate, local.updatedDate),
      };
    });
    if (options.authoritativeServer) {
      this.reconcileStaleLocalTrashMeta(reconciledDocs, localTrash, now);
    }
    return reconciledDocs;
  }

  private applyPendingLocalTrashWrite(
    doc: AccessibleDocMeta,
    local: { trashed: boolean; updatedDate: number } | undefined,
    now: number
  ): AccessibleDocMeta {
    const pending = this.pendingLocalTrashWrites.get(doc.id);
    if (!pending) {
      return doc;
    }
    if (doc.trashed === pending.trashed) {
      this.pendingLocalTrashWrites.delete(doc.id);
      return doc;
    }
    if (now >= pending.expiresAt) {
      this.pendingLocalTrashWrites.delete(doc.id);
      return doc;
    }
    return {
      ...doc,
      trashed: pending.trashed,
      updatedDate: Math.max(doc.updatedDate, local?.updatedDate ?? 0),
    };
  }

  private isHardDeletedDocId(docId: string): boolean {
    return (
      this.pendingDeletions.has(docId) || this.confirmedDeletedIds.has(docId)
    );
  }

  private filterHardDeletedDocs(
    docs: AccessibleDocMeta[]
  ): AccessibleDocMeta[] {
    return docs.filter(doc => !this.isHardDeletedDocId(doc.id));
  }

  private readLocalTrashMeta(): Map<
    string,
    { trashed: boolean; updatedDate: number }
  > {
    const workspace = this.workspaceService.workspace;
    const pages = workspace?.rootYDoc?.getMap('meta')?.get('pages');
    if (!pages || typeof pages !== 'object' || !('forEach' in pages)) {
      return new Map();
    }

    const result = new Map<string, { trashed: boolean; updatedDate: number }>();
    try {
      (
        pages as {
          forEach(callback: (value: unknown) => void): void;
        }
      ).forEach(value => {
        if (!value || typeof value !== 'object' || !('get' in value)) {
          return;
        }
        const yMap = value as { get(key: string): unknown };
        const id = yMap.get('id');
        if (typeof id !== 'string') {
          return;
        }
        const trash = yMap.get('trash');
        if (trash !== undefined) {
          const trashDate = yMap.get('trashDate');
          const updatedDate = yMap.get('updatedDate');
          result.set(id, {
            trashed: !!trash,
            updatedDate: Math.max(
              typeof updatedDate === 'number' ? updatedDate : 0,
              typeof trashDate === 'number' ? trashDate : 0
            ),
          });
        }
      });
    } catch {
      return new Map();
    }

    return result;
  }

  private reconcileStaleLocalTrashMeta(
    docs: AccessibleDocMeta[],
    localTrash: Map<string, { trashed: boolean; updatedDate: number }>,
    now: number
  ): void {
    const toTrash: string[] = [];
    const toRestore: string[] = [];
    for (const doc of docs) {
      const local = localTrash.get(doc.id);
      if (!local || local.trashed === doc.trashed) {
        continue;
      }
      const pending = this.pendingLocalTrashWrites.get(doc.id);
      if (pending && now < pending.expiresAt) {
        continue;
      }
      if (pending) {
        this.pendingLocalTrashWrites.delete(doc.id);
      }
      if (doc.trashed) {
        toTrash.push(doc.id);
      } else {
        toRestore.push(doc.id);
      }
    }
    if (toTrash.length > 0) {
      this.writeLocalTrashMeta(toTrash, true, now);
    }
    if (toRestore.length > 0) {
      this.writeLocalTrashMeta(toRestore, false, now);
    }
  }

  private getLocalPagesArray(create: boolean): YArray<YMap<unknown>> | null {
    const workspace = this.workspaceService.workspace;
    const rootYDoc = workspace?.rootYDoc;
    if (!rootYDoc) {
      return null;
    }

    const meta = rootYDoc.getMap('meta');
    const existing = meta.get('pages');
    if (existing instanceof YArray) {
      return existing as YArray<YMap<unknown>>;
    }
    if (!create || existing !== undefined) {
      return null;
    }

    const pages = new YArray<YMap<unknown>>();
    meta.set('pages', pages);
    return pages;
  }

  private findLocalDocMeta(docId: string): {
    pages: YArray<YMap<unknown>>;
    index: number;
    meta: YMap<unknown>;
  } | null {
    const pages = this.getLocalPagesArray(false);
    if (!pages) {
      return null;
    }

    for (let index = 0; index < pages.length; index++) {
      const meta = pages.get(index);
      if (meta instanceof YMap && meta.get('id') === docId) {
        return { pages, index, meta };
      }
    }
    return null;
  }

  private writeLocalTrashMeta(
    docIds: string[],
    trashed: boolean,
    now: number,
    options: { createMissing?: boolean } = {}
  ): void {
    const workspace = this.workspaceService.workspace;
    const rootYDoc = workspace?.rootYDoc;
    if (!rootYDoc) {
      return;
    }

    const cacheById = new Map(
      this.cache$.value.map((doc: AccessibleDocMeta) => [doc.id, doc])
    );

    transact(
      rootYDoc,
      () => {
        const pages = this.getLocalPagesArray(true);
        if (!pages) {
          return;
        }

        for (const docId of docIds) {
          const cacheMeta = cacheById.get(docId);
          let entry = this.findLocalDocMeta(docId)?.meta ?? null;
          if (!entry) {
            if (!cacheMeta && !options.createMissing) {
              continue;
            }
            entry = new YMap<unknown>();
            entry.set('id', docId);
            entry.set('title', cacheMeta?.title ?? '');
            entry.set('createDate', cacheMeta?.createDate ?? now);
            entry.set(
              'updatedDate',
              Math.max(now, cacheMeta?.updatedDate ?? 0)
            );
            pages.push([entry]);
          }

          entry.set('trash', trashed);
          entry.set('updatedDate', Math.max(now, cacheMeta?.updatedDate ?? 0));
          if (trashed) {
            entry.set('trashDate', now);
          } else {
            entry.delete('trashDate');
          }
        }
      },
      { force: true }
    );
  }

  private restoreLocalTrashMeta(
    entry: AccessibleDocTrashSnapshot['entries'][number]
  ): void {
    const workspace = this.workspaceService.workspace;
    const rootYDoc = workspace?.rootYDoc;
    if (!rootYDoc) {
      return;
    }

    transact(
      rootYDoc,
      () => {
        const localMeta = this.findLocalDocMeta(entry.id);
        if (!localMeta) {
          return;
        }
        if (!entry.localMetaExisted) {
          localMeta.pages.delete(localMeta.index, 1);
          return;
        }
        if (entry.localTrash === undefined) {
          localMeta.meta.delete('trash');
        } else {
          localMeta.meta.set('trash', entry.localTrash);
        }
        if (entry.localTrashDate === undefined) {
          localMeta.meta.delete('trashDate');
        } else {
          localMeta.meta.set('trashDate', entry.localTrashDate);
        }
      },
      { force: true }
    );
  }

  private clearLocalTrashMeta(docIds: string[]): void {
    const workspace = this.workspaceService.workspace;
    const rootYDoc = workspace?.rootYDoc;
    if (!rootYDoc) {
      return;
    }

    const targetIds = new Set(docIds);
    transact(
      rootYDoc,
      () => {
        const pages = this.getLocalPagesArray(false);
        if (!pages) {
          return;
        }
        for (let index = 0; index < pages.length; index++) {
          const meta = pages.get(index);
          if (!(meta instanceof YMap)) {
            continue;
          }
          const id = meta.get('id');
          if (typeof id !== 'string' || !targetIds.has(id)) {
            continue;
          }
          meta.delete('trash');
          meta.delete('trashDate');
        }
      },
      { force: true }
    );
  }

  /** Merge a batch of docs into cache$ by ID (no duplicates, preserves local-only docs) */
  private mergeBatchIntoCache(batchDocs: AccessibleDocMeta[]): void {
    const current = new Map(
      this.cache$.value.map((d: AccessibleDocMeta) => [d.id, d])
    );
    for (const doc of batchDocs) {
      if (
        this.pendingDeletions.has(doc.id) ||
        this.confirmedDeletedIds.has(doc.id)
      )
        continue;
      const existing = current.get(doc.id);
      const pendingTitle = this.pendingLocalTitleWrites.get(doc.id);
      if (pendingTitle) {
        if (doc.title === pendingTitle.title) {
          this.pendingLocalTitleWrites.delete(doc.id);
        } else if (Date.now() < pendingTitle.expiresAt) {
          current.set(doc.id, {
            ...doc,
            title: pendingTitle.title,
            updatedDate: Math.max(
              doc.updatedDate,
              existing?.updatedDate ?? doc.updatedDate
            ),
          });
          continue;
        } else {
          this.pendingLocalTitleWrites.delete(doc.id);
        }
      }
      current.set(doc.id, doc);
    }
    const nextDocs = [...current.values()];
    this.publishCache(nextDocs, 'merge-batch');
  }

  private async syncBatchToMetadataStore(
    workspaceId: string,
    batchDocs: AccessibleDocMeta[]
  ): Promise<void> {
    const metadataStore = this.metadataStore;
    if (!metadataStore) return;

    try {
      await metadataStore.applyServerNodes(
        batchDocs
          .filter(doc => !this.isHardDeletedDocId(doc.id))
          .map(doc => this.docMetaToMetadataNode(workspaceId, doc, 'fresh')),
        {
          token: this.metadataDeltaToken,
          cursor: null,
          lastSuccessfulSyncAt: new Date().toISOString(),
        }
      );
    } catch {
      // Local metadata write failure — non-fatal
    }
  }

  private async removeRevokedDocs(
    docIds: readonly string[],
    options: { cachedBeforeFetch?: ReadonlySet<string> } = {}
  ): Promise<void> {
    const revokedIds = docIds.filter(
      docId =>
        (this.getCachedDoc(docId) ||
          options.cachedBeforeFetch?.has(docId) === true) &&
        !this.locallyCreatedIds.has(docId) &&
        !this.isHardDeletedDocId(docId)
    );
    if (revokedIds.length === 0) {
      return;
    }

    const revokedIdSet = new Set(revokedIds);
    this.publishCache(
      this.cache$.value.filter(doc => !revokedIdSet.has(doc.id)),
      'remove-revoked-docs'
    );

    const metadataStore = this.metadataStore;
    if (metadataStore) {
      try {
        await metadataStore.removeNodes(revokedIds, {
          lastSuccessfulSyncAt: new Date().toISOString(),
        });
      } catch {
        // Local metadata write failure — non-fatal
      }
    }

    this.revokedDocIds$.setValue(revokedIds);
  }

  removeRevokedDocsLocally(docIds: readonly string[]): void {
    void this.removeRevokedDocs(docIds);
  }

  /**
   * Add a locally-created doc to the cache immediately.
   * This ensures the doc appears in the sidebar before the server confirms it.
   * On next fetchAccessibleDocs(), server data will merge with/replace this entry.
   */
  addLocalDoc(
    id: string,
    initialMeta: { title?: string; trash?: boolean; updatedDate?: number } = {}
  ): void {
    this.locallyCreatedIds.add(id);
    this.confirmedDeletedIds.delete(id);
    const existing = this.cache$.value;
    if (existing.some((d: AccessibleDocMeta) => d.id === id)) return;
    this.isPopulated$.setValue(true);
    const now = Date.now();
    const title = initialMeta.title ?? '';
    const localDoc: AccessibleDocMeta = {
      id,
      title,
      trashed: initialMeta.trash ?? false,
      createDate: now,
      updatedDate: initialMeta.updatedDate ?? now,
      isDemo: false,
      permissionGraphVersion: this.permissionGraphVersion,
    };
    this.publishCache([...existing, localDoc], 'add-local-doc');

    const wsId = this.workspaceId;
    const metadataStore = this.metadataStore;
    if (wsId && metadataStore) {
      const localNode = this.docMetaToMetadataNode(wsId, localDoc, 'local');
      const mutation = this.metadataPendingMutation(
        localDoc,
        'create',
        localNode
      );
      if (mutation) {
        void this.queueMetadataWrite(() =>
          metadataStore.applyLocalMutation([localNode], mutation)
        );
      }
    }
  }

  /**
   * Update metadata for a cached doc (e.g. title change from Y.js).
   * Immediately reflects in all observables derived from cache$.
   * Only updates fields that are present in the partial — other fields are preserved.
   */
  updateDocMeta(
    docId: string,
    meta: Partial<Omit<AccessibleDocMeta, 'id'>>,
    options: UpdateDocMetaOptions = {}
  ): void {
    const docs = this.cache$.value;
    const index = docs.findIndex((d: AccessibleDocMeta) => d.id === docId);
    if (index === -1) return;
    const persistTrashState =
      options.persistTrashState === true && meta.trashed !== undefined;
    const now = Date.now();

    if (meta.title !== undefined) {
      this.pendingLocalTitleWrites.set(docId, {
        title: meta.title,
        expiresAt: now + LOCAL_TITLE_WRITE_PROTECTION_MS,
      });
    }

    const updated = [...docs];
    updated[index] = {
      ...updated[index],
      ...meta,
      ...(persistTrashState
        ? { updatedDate: Math.max(updated[index].updatedDate, now) }
        : {}),
    };
    this.publishCache(
      updated,
      `update-doc-meta:${Object.keys(meta).sort().join('+') || 'empty'}`
    );

    if (meta.title !== undefined) {
      void this.enqueueMetadataPending(updated[index], 'rename', {
        title: meta.title,
      });
    }

    if (!persistTrashState) {
      return;
    }

    this.pendingLocalTrashWrites.set(docId, {
      trashed: meta.trashed as boolean,
      expiresAt: now + LOCAL_TRASH_WRITE_PROTECTION_MS,
    });
    this.writeLocalTrashMeta([docId], meta.trashed as boolean, now);
    void this.enqueueMetadataPending(
      updated[index],
      meta.trashed ? 'trash' : 'restore',
      {
        trashedAt: meta.trashed ? new Date(now).toISOString() : null,
      }
    );
  }

  /**
   * End optimistic title protection once the matching server mutation has
   * completed. Keeping the guard until its timeout would suppress a later
   * rename from another collaborator and leave every remote title consumer
   * on the stale local value.
   */
  confirmLocalTitleWrite(docId: string, title: string): void {
    const pending = this.pendingLocalTitleWrites.get(docId);
    if (pending?.title === title) {
      this.pendingLocalTitleWrites.delete(docId);
    }
  }

  captureTrashSnapshot(docIds: string[]): AccessibleDocTrashSnapshot {
    const uniqueDocIds = [...new Set(docIds)];
    const cacheById = new Map(
      this.cache$.value.map((doc: AccessibleDocMeta) => [doc.id, doc])
    );
    return {
      entries: uniqueDocIds.map(id => {
        const localMeta = this.findLocalDocMeta(id);
        return {
          id,
          cacheMeta: cacheById.get(id) ?? null,
          localMetaExisted: localMeta !== null,
          localTrash:
            localMeta?.meta.get('trash') === undefined
              ? undefined
              : !!localMeta.meta.get('trash'),
          localTrashDate: localMeta?.meta.get('trashDate'),
        };
      }),
    };
  }

  setDocsTrashState(docIds: string[], trashed: boolean): void {
    const uniqueDocIds = [...new Set(docIds)];
    if (uniqueDocIds.length === 0) {
      return;
    }

    const now = Date.now();
    const targetIds = new Set(uniqueDocIds);
    for (const docId of uniqueDocIds) {
      this.pendingLocalTrashWrites.set(docId, {
        trashed,
        expiresAt: now + LOCAL_TRASH_WRITE_PROTECTION_MS,
      });
    }
    let changed = false;
    const updatedDocs = this.cache$.value.map(doc => {
      if (!targetIds.has(doc.id)) {
        return doc;
      }
      if (doc.trashed === trashed && doc.updatedDate >= now) {
        return doc;
      }
      changed = true;
      return {
        ...doc,
        trashed,
        updatedDate: Math.max(now, doc.updatedDate),
      };
    });

    if (changed) {
      this.publishCache(updatedDocs, 'set-docs-trash-state');
    }
    this.writeLocalTrashMeta(uniqueDocIds, trashed, now, {
      createMissing: true,
    });

    if (changed) {
      for (const doc of updatedDocs.filter(doc => targetIds.has(doc.id))) {
        void this.enqueueMetadataPending(doc, trashed ? 'trash' : 'restore', {
          trashedAt: trashed ? new Date(now).toISOString() : null,
        });
      }
    }
  }

  /**
   * Apply a trash state that was already committed by a server lifecycle
   * operation. Unlike setDocsTrashState(), this must not enqueue another
   * mutation: folder restore/delete endpoints already update descendants.
   */
  applyServerConfirmedTrashState(
    docIds: readonly string[],
    trashed: boolean
  ): void {
    const uniqueDocIds = [...new Set(docIds)];
    if (uniqueDocIds.length === 0) {
      return;
    }

    const now = Date.now();
    const targetIds = new Set(uniqueDocIds);
    for (const docId of uniqueDocIds) {
      this.pendingLocalTrashWrites.delete(docId);
    }

    const updatedDocs = this.cache$.value.map(doc =>
      targetIds.has(doc.id)
        ? {
            ...doc,
            trashed,
            updatedDate: Math.max(now, doc.updatedDate),
          }
        : doc
    );
    this.publishCache(updatedDocs, 'server-confirmed-trash-state');
    this.writeLocalTrashMeta(uniqueDocIds, trashed, now, {
      createMissing: true,
    });

    const workspaceId = this.workspaceService.workspace.id;
    void this.syncBatchToMetadataStore(
      workspaceId,
      updatedDocs.filter(doc => targetIds.has(doc.id))
    );
  }

  rollbackDocsTrashState(docIds: string[], trashed: boolean): void {
    const uniqueDocIds = [...new Set(docIds)];
    if (uniqueDocIds.length === 0) {
      return;
    }

    const now = Date.now();
    const targetIds = new Set(uniqueDocIds);
    for (const docId of uniqueDocIds) {
      this.pendingLocalTrashWrites.delete(docId);
    }
    let changed = false;
    const updatedDocs = this.cache$.value.map(doc => {
      if (!targetIds.has(doc.id)) {
        return doc;
      }
      changed = true;
      return {
        ...doc,
        trashed,
        updatedDate: Math.max(now, doc.updatedDate),
      };
    });

    if (changed) {
      this.publishCache(updatedDocs, 'rollback-docs-trash-state');
    }
    this.clearLocalTrashMeta(uniqueDocIds);

    if (changed) {
      for (const docId of uniqueDocIds) {
        const metadataStore = this.metadataStore;
        if (metadataStore) {
          void this.queueMetadataWrite(() =>
            metadataStore.rejectPendingForNode(docId, ['trash', 'restore'])
          );
        }
      }
    }
  }

  restoreTrashSnapshot(snapshot: AccessibleDocTrashSnapshot): void {
    const snapshotIds = new Set(snapshot.entries.map(entry => entry.id));
    for (const entry of snapshot.entries) {
      this.pendingLocalTrashWrites.delete(entry.id);
    }
    const current = new Map(
      this.cache$.value.map((doc: AccessibleDocMeta) => [doc.id, doc])
    );
    for (const entry of snapshot.entries) {
      if (entry.cacheMeta) {
        current.set(entry.id, entry.cacheMeta);
      } else {
        current.delete(entry.id);
      }
      this.restoreLocalTrashMeta(entry);
    }

    const restoredDocs = [...current.values()];
    this.publishCache(restoredDocs, 'restore-trash-snapshot');

    for (const docId of snapshotIds) {
      const metadataStore = this.metadataStore;
      if (metadataStore) {
        void this.queueMetadataWrite(() =>
          metadataStore.rejectPendingForNode(docId, ['trash', 'restore'])
        );
      }
    }
  }

  private enqueueMetadataPending(
    doc: AccessibleDocMeta,
    op: WorkspaceMetadataPendingMutation['op'],
    patch: Partial<WorkspaceListMetadata>
  ): Promise<void> {
    const store = this.metadataStore;
    const mutation = this.metadataPendingMutation(doc, op, patch);
    if (!store || !mutation) {
      return Promise.resolve();
    }
    return this.queueMetadataWrite(() => store.enqueuePending(mutation));
  }

  private metadataPendingMutation(
    doc: AccessibleDocMeta,
    op: WorkspaceMetadataPendingMutation['op'],
    patch: Partial<WorkspaceListMetadata>
  ): WorkspaceMetadataPendingMutation | null {
    const server = this.workspaceServerService.server;
    const accountId = this.accountId;
    const workspaceId = this.workspaceId;
    const metadataStore = this.metadataStore;
    if (!server || !accountId || !workspaceId || !metadataStore) {
      return null;
    }
    const state = metadataStore.getShapeState();
    return {
      accountId,
      principalId: accountId,
      serverId: server.id,
      serverBaseUrl: server.baseUrl,
      visibilityMode: 'user_effective',
      tenantId: state.tenantId,
      shapeKey: state.shapeKey,
      schemaVersion: state.schemaVersion,
      permissionGraphVersion: state.permissionGraphVersion,
      localMutationId: this.metadataMutationId(doc.id, op),
      workspaceId,
      nodeId: doc.id,
      op,
      baseRevision: new Date(doc.updatedDate).toISOString(),
      patch,
      status: 'queued',
      createdAt: new Date().toISOString(),
    };
  }

  private metadataMutationId(
    docId: string,
    op: WorkspaceMetadataPendingMutation['op']
  ): string {
    const operation = op === 'trash' || op === 'restore' ? 'trash-state' : op;
    return `doc-${operation}:${docId}`;
  }

  private queueMetadataWrite(write: () => Promise<void>): Promise<void> {
    const next = this.metadataPendingWrite.then(write);
    this.metadataPendingWrite = next.catch(error => {
      console.error('Failed to persist workspace metadata mutation:', error);
    });
    return next;
  }

  flushMetadataPendingWrites(): Promise<void> {
    return this.metadataPendingWrite;
  }

  async removeDoc(docId: string): Promise<void> {
    if (this.pendingDeletions.has(docId)) {
      return;
    }

    const cachedDoc = this.getCachedDoc(docId);
    const wasLocallyCreated = this.locallyCreatedIds.has(docId);
    this.pendingDeletions.add(docId);
    this.removeDocFromLocalCache(docId);

    const metadataStore = this.metadataStore;
    const workspaceId = this.workspaceId;
    const mutation = cachedDoc
      ? this.metadataPendingMutation(cachedDoc, 'delete', {})
      : null;
    try {
      if (metadataStore && workspaceId && mutation && cachedDoc) {
        const node = this.docMetaToMetadataNode(
          workspaceId,
          cachedDoc,
          wasLocallyCreated ? 'local' : 'fresh'
        );
        await this.queueMetadataWrite(() =>
          metadataStore.applyLocalMutation([node], mutation)
        );
      }
    } catch (error) {
      this.pendingDeletions.delete(docId);
      if (cachedDoc) {
        this.publishCache(
          [...this.cache$.value, cachedDoc],
          'permanent-delete-rollback'
        );
      }
      if (wasLocallyCreated) {
        this.locallyCreatedIds.add(docId);
      }
      throw error;
    }
    void this.persistDeleteToServer(docId);
  }

  async removeDocsLocallyAfterFolderPermanentDelete(
    docIds: string[]
  ): Promise<void> {
    const uniqueDocIds = [...new Set(docIds)];
    if (uniqueDocIds.length === 0) {
      return;
    }

    const cacheById = new Map(
      this.cache$.value.map((doc: AccessibleDocMeta) => [doc.id, doc])
    );
    for (const docId of uniqueDocIds) {
      if (!this.folderPermanentDeleteSnapshots.has(docId)) {
        const localMeta = this.findLocalDocMeta(docId);
        this.folderPermanentDeleteSnapshots.set(docId, {
          id: docId,
          cacheMeta: cacheById.get(docId) ?? null,
          locallyCreated: this.locallyCreatedIds.has(docId),
          localMetaExisted: localMeta !== null,
          localTrash:
            localMeta?.meta.get('trash') === undefined
              ? undefined
              : !!localMeta.meta.get('trash'),
          localTrashDate: localMeta?.meta.get('trashDate'),
        });
      }
      this.confirmPermanentDelete(docId);
      this.removeDocFromLocalCache(docId);
    }
    this.clearLocalTrashMeta(uniqueDocIds);
    await Promise.all(
      uniqueDocIds.map(docId => this.removeDocFromMetadataStore(docId))
    );
    this.emitPermanentDeleteLifecycle(uniqueDocIds);
  }

  async recoverDocsAfterFailedFolderPermanentDelete(
    docIds: string[]
  ): Promise<void> {
    const uniqueDocIds = [...new Set(docIds)];
    if (uniqueDocIds.length === 0) {
      return;
    }

    for (const docId of uniqueDocIds) {
      this.confirmedDeletedIds.delete(docId);
      this.pendingDeletions.delete(docId);
      this.signaledPermanentDeleteIds.delete(docId);
    }

    await this.fetchAccessibleDocs().catch(() => {});

    const serverRecoveredDocIds = new Set(
      this.cache$.value.map((doc: AccessibleDocMeta) => doc.id)
    );
    const current = new Map(
      this.cache$.value.map((doc: AccessibleDocMeta) => [doc.id, doc])
    );
    const restoredDocs: AccessibleDocMeta[] = [];

    for (const docId of uniqueDocIds) {
      const snapshot = this.folderPermanentDeleteSnapshots.get(docId);
      if (!snapshot) {
        continue;
      }
      if (serverRecoveredDocIds.has(docId)) {
        this.locallyCreatedIds.delete(docId);
        this.folderPermanentDeleteSnapshots.delete(docId);
        continue;
      }
      if (snapshot.cacheMeta && !current.has(docId)) {
        current.set(docId, snapshot.cacheMeta);
        restoredDocs.push(snapshot.cacheMeta);
      }
      if (snapshot.locallyCreated) {
        this.locallyCreatedIds.add(docId);
      }
      this.restoreLocalTrashMeta(snapshot);
      this.folderPermanentDeleteSnapshots.delete(docId);
    }

    if (restoredDocs.length === 0) {
      return;
    }

    const docs = [...current.values()];
    this.publishCache(docs, 'folder-delete-recovery');
    this.isPopulated$.setValue(true);

    const wsId = this.workspaceId;
    if (wsId) {
      await this.syncBatchToMetadataStore(wsId, restoredDocs).catch(() => {});
    }
  }

  completeDocsAfterSuccessfulFolderPermanentDelete(docIds: string[]): void {
    const uniqueDocIds = [...new Set(docIds)];
    for (const docId of uniqueDocIds) {
      this.folderPermanentDeleteSnapshots.delete(docId);
    }
  }

  private processPendingDeletions(): void {
    for (const docId of this.pendingDeletions) {
      void this.persistDeleteToServer(docId);
    }
  }

  private async persistDeleteToServer(docId: string): Promise<boolean> {
    if (!this.permanentDeleteHandler) {
      return false;
    }

    try {
      await this.permanentDeleteHandler(docId);
      const metadataStore = this.metadataStore;
      if (metadataStore) {
        await this.queueMetadataWrite(() =>
          metadataStore.ackPending(this.metadataMutationId(docId, 'delete'))
        );
      }
      this.confirmedDeletedIds.add(docId);
      this.pendingDeletions.delete(docId);
      return true;
    } catch (err) {
      console.error('Failed to persist permanent doc delete:', err);
      return false;
    }
  }

  private removeDocFromLocalCache(docId: string): void {
    const docs = this.cache$.value;
    this.publishCache(
      docs.filter((d: AccessibleDocMeta) => d.id !== docId),
      'remove-local-doc'
    );
    this.locallyCreatedIds.delete(docId);
    this.pendingLocalTitleWrites.delete(docId);
  }

  /**
   * Remove a not-yet-server-confirmed local doc from the local metadata cache.
   * This is for aborted creation flows only; it must not call server delete
   * mutations because the server may not know the doc yet.
   */
  discardLocalDoc(docId: string): void {
    this.removeDocFromLocalCache(docId);
    void this.removeDocFromMetadataStore(docId);
  }

  private async removeDocFromMetadataStore(docId: string): Promise<void> {
    const metadataStore = this.metadataStore;
    if (!metadataStore) {
      return;
    }
    try {
      await metadataStore.removeNodes([docId]);
    } catch {
      // Local metadata failure — non-fatal, cache$ is already updated
    }
  }

  /**
   * Synchronous lookup of a cached doc by ID.
   * Used by GuardStore as a local metadata hint; permission decisions come from
   * PermissionSnapshotStore.
   */
  getCachedDoc(docId: string): AccessibleDocMeta | null {
    return (
      this.cache$.value.find((d: AccessibleDocMeta) => d.id === docId) ?? null
    );
  }

  /**
   * Synchronous local-first snapshot for first-render observable seeding.
   * The returned array is immutable by convention; later cache generations
   * replace it instead of mutating it in place.
   */
  getCachedDocs(): AccessibleDocMeta[] {
    return this.cache$.value;
  }

  /**
   * Observable of accessible doc IDs.
   */
  watchAccessibleDocIds(): Observable<string[]> {
    this.ensureFetched();
    return this.cache$.pipe(
      map((docs: AccessibleDocMeta[]) =>
        docs.map((d: AccessibleDocMeta) => d.id)
      )
    );
  }

  /**
   * Observable of accessible doc metadata for a specific doc.
   */
  watchAccessibleDocMeta(docId: string): Observable<AccessibleDocMeta | null> {
    this.ensureFetched();
    return this.cache$.pipe(
      map(
        (docs: AccessibleDocMeta[]) =>
          docs.find((d: AccessibleDocMeta) => d.id === docId) ?? null
      )
    );
  }

  /**
   * Observable of all accessible doc metadata.
   */
  watchAllAccessibleDocs(): Observable<AccessibleDocMeta[]> {
    this.ensureFetched();
    return this.cache$.pipe(map((docs: AccessibleDocMeta[]) => docs));
  }

  watchTrashedDocIds(): Observable<string[]> {
    this.ensureFetched();
    return this.cache$.pipe(
      map((docs: AccessibleDocMeta[]) =>
        docs.filter(doc => doc.trashed).map(doc => doc.id)
      ),
      distinctUntilChanged(
        (previous, current) =>
          previous.length === current.length &&
          previous.every((id, index) => id === current[index])
      )
    );
  }
}

function isInvalidMetadataDeltaTokenError(error: unknown): boolean {
  const body =
    error instanceof WorkspaceApiError
      ? (error.responseBody as Record<string, unknown> | null)
      : null;
  const nestedError = body?.error as Record<string, unknown> | undefined;
  const message = nestedError?.message ?? body?.message;
  return (
    error instanceof WorkspaceApiError &&
    error.status === 400 &&
    message === 'Invalid metadata change token'
  );
}

function docSortKey(doc: AccessibleDocMeta): string {
  const timestamp = Number.isFinite(doc.updatedDate) ? doc.updatedDate : 0;
  const inverted = Number.MAX_SAFE_INTEGER - Math.max(0, timestamp);
  return `${String(inverted).padStart(16, '0')}:${doc.id}`;
}
